import Button from "components/common/button/Button";
import HTMLCode from "components/common/HTMLCode";
import If from "components/common/If";
import Translate from "components/common/Translate";
import { getFormContext } from "config/FormContext";
import { useContext, useEffect, useRef, useState } from "react";
import { BaseInputPropsType, InputErrorType } from "types/form-types";
import { validateInput } from "utilities/input-utilities";
import InputErrors from "./input-common/InputErrors";
import InputLabel from "./input-common/InputLabel";

type CheckboxGroupInputPropsType<T> = Omit<BaseInputPropsType<HTMLInputElement, T[]>, 'onChange'> & {
    data: T[],
    itemKey?: (i:T) => string | number;
    display?: (i:T) => string,
    min?: number
    max?: number,
    onChange:(value: T[] | undefined, event: React.ChangeEvent<HTMLInputElement>, current: T) => void
    bulkChange?: (value: T[], event: React.MouseEvent) => void
}



function CheckboxGroupInput<T>(props: CheckboxGroupInputPropsType<T>) {
    const input = useRef<HTMLInputElement>(null);
    const inputWrapper = useRef<HTMLDivElement>(null);
    let [touched, setTouched] = useState(props.touched ? true : false);
    let [errors, setErrors] = useState<InputErrorType[]>([]);
    const formContext = getFormContext(useContext);
   
    useEffect(() => {
        validate(!touched);
    }, []);

    useEffect(() => {
        validate(!touched);
    }, [props.value]);

    useEffect(() => {
        setTouched(formContext.submitted);
        validate(!formContext.submitted);
    }, [formContext.submitted])

    const onChange = (e: React.ChangeEvent<HTMLInputElement>, item: T) => {
        const isCheck = e.target.checked;
        let currentValue = props.value ? [...props.value] : [];
        if(isCheck) {
            currentValue.push(item);
        } else {
            const itemKey = getKey(item);
            currentValue = currentValue.filter(c => getKey(c) !== itemKey);
        }

        if (typeof props.onChange == 'function') {
            props.onChange(currentValue, e, item);
        }

        setTouched(true);
    };

    const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setTouched(true);
        validate();
        if (typeof props.onBlur == 'function') {
            props.onBlur(props.value, e);
        }
    }

    function validate(soft?: boolean) {
        let currentCustomErrors = props.customErrors ? [...props.customErrors] : [];
        if(props.min) {
            currentCustomErrors.push({
                message: 'form.errors.rangeUnderflow',
                name: 'rangeUnderflow',
                validate(value) {
                    return !value?.length || value.length >= props.min!;
                },
            })
        }

        if(props.max) {
            currentCustomErrors.push({
                message: 'form.errors.rangeOverflow',
                name: 'rangeOverflow',
                validate(value) {
                    return !value?.length || value.length <= props.max!;
                },
            })
        }

        if(props.required) {
            currentCustomErrors.push({
                message: 'form.errors.valueMissing',
                name: 'valueMissing',
                validate(value) {
                    return value?.length ? true : false;
                },
            })
        }

        setErrors(validateInput<T[], HTMLInputElement>({
            value: props.value,
            input: input?.current || undefined,
            inputWrapper: inputWrapper?.current || undefined,
            customErrors: currentCustomErrors,
            soft: soft ? true : false
        }) || []);
    }

    const display = (item: T) => {
        if(typeof props.display == 'function') return props.display(item);
        if(typeof item === 'object') return (item as any).name;
        return item;
    }

    const getKey = (item: T) => {
        if(typeof props.itemKey == 'function') return props.itemKey(item);
        if(typeof item === 'object') return (item as any).id;
        return item;
    }

    return (
        <div className="app-input-wrapper checkbox" ref={inputWrapper}>
            <input ref={input} type='checkbox' className="hidden" placeholder="shit" />
            <InputLabel label={props.label} required={props.required} className='my-auto' />
            <ul>
                {props.data.map((c) => {
                    return (
                        <li key={getKey(c)}>
                            <label className="inline-flex">
                                <input
                                    type="checkbox"
                                    name={props.name}
                                    autoFocus={props.autoFocus}
                                    checked={props.value && props.value?.length && props.value.map(i => getKey(i)).indexOf(getKey(c)) > -1 ? true : false}
                                    onChange={v => onChange(v, c)}
                                    placeholder="checkbox"
                                    onBlur={onBlur}
                                    disabled={props.disabled}
                                    readOnly={props.readonly}
                                    className="accent-primary"
                                />
                                <HTMLCode />
                                <InputLabel label={display(c)} removeDoublePoints className='my-auto' />
                            </label>
                        </li>
                    );
                })}
            </ul>
            <If condition={typeof props.bulkChange == 'function'}>
                <div>
                    <Button theme="link" title="select-all" onClick={e => props.bulkChange!(props.data, e)}>
                        <Translate i18nKey="common.select-all" />
                    </Button>
                    <Button theme="link" title="select-all" onClick={e => props.bulkChange!([], e)}>
                        <Translate i18nKey="common.deselect-all" />
                    </Button>
                </div>
            </If>
            <InputErrors errors={errors} />
        </div>
    );
}

export default CheckboxGroupInput;
